import React, { useContext } from 'react';
import { Box, Flex, Icon, List, ListItem, Text } from '@chakra-ui/react';
import {
  VAdultCareIcon,
  VAssessmentIcon,
  VBreakupIcon,
  VCalendarIcon,
  VFinancialHelpIcon,
  VHomeIcon,
  VInsuranceIcon,
  VLineChartIcon,
  VProviderIcon,
  VInternationalIcon,
  VUploadIcon,
  VWorkplaceIcon,
} from '@springcare/verdant-icons-react';
import { useRouter } from 'next/router';
import { MdOutlineCircle, MdFormatListBulletedAdd, MdOutlineHandshake, MdBuild } from 'react-icons/md';
import { TbAccessPoint, TbPresentation, TbUsers, TbUserSearch, TbClipboardSmile, TbCompass } from 'react-icons/tb';

const MdOutlineCircleIcon = MdOutlineCircle as React.ElementType;
const MdFormatListBulletedAddIcon = MdFormatListBulletedAdd as React.ElementType;
const MdOutlineHandshakeIcon = MdOutlineHandshake as React.ElementType;
const MdBuildIcon = MdBuild as React.ElementType;
const TbAccessPointIcon = TbAccessPoint as React.ElementType;
const TbPresentationIcon = TbPresentation as React.ElementType;
const TbUsersIcon = TbUsers as React.ElementType;
const TbUserSearchIcon = TbUserSearch as React.ElementType;
const TbClipboardSmileIcon = TbClipboardSmile as React.ElementType;
const TbCompassIcon = TbCompass as React.ElementType;

import { CDX_REPORTING_ADMIN, MAINTENANCE_TASK_RUNNER } from 'constants/roles';
import routes from 'routes';
import { FLAGS, useFeatureFlag } from 'utils/launchdarkly';
import { getCompassUrl } from 'modules/shared/utils/redirectToCompass';
import { PROVIDER_APP_NAME } from 'constants/terminologyRename';
import { NavLink } from 'modules/shared/components/NavLink';
import { hoverColor } from './constants/styles';
import { AuthContext } from 'modules/shared/context/AuthContext';
import { usePermissions } from 'modules/shared/hooks/usePermissions';
import * as permissions from 'modules/shared/constants/permissions';

interface IProps {
  menuOpen: boolean;
}

interface IMenuItem {
  alias: string;
  icon: React.ElementType;
  text: string;
  hide?: boolean;
  href?: string;
}

export const SideMenuItems: React.FC<React.PropsWithChildren<IProps>> = ({ menuOpen }) => {
  const router = useRouter();
  const { user } = useContext(AuthContext);

  // Role based access control
  const hasAppointmentsAccess = usePermissions([
    permissions.APPOINTMENTS_READ,
    permissions.APPOINTMENTS_OFFSHORE_READ,
  ]).some((p) => !!p);
  const hasAssessmentsAccess = usePermissions([
    permissions.ASSESSMENTS_READ,
    permissions.ASSESSMENTS_OFFSHORE_READ,
    permissions.ASSESSMENTS_TRIAL_READ,
  ]).some((p) => !!p);
  const hasCareProvidersAccess = usePermissions([
    permissions.CARE_PROVIDERS_READ,
    permissions.CARE_PROVIDERS_OFFSHORE_READ,
  ]).some(Boolean);
  const [hasSSOAccess, hasConnectedBenefitsAccess, canReadDemoAccounts] = usePermissions([
    permissions.SSO_CLIENTS_READ,
    permissions.CONNECTED_BENEFITS_READ,
    permissions.DEMO_ACCOUNTS_READ,
  ]);

  const hasChannelPartnersAccess = usePermissions([
    permissions.CHANNEL_PARTNERS_READ,
    permissions.CHANNEL_PARTNERS_OFFSHORE_READ,
  ]).some(Boolean);
  const hasCensusIngestionsAccess = usePermissions([
    permissions.CENSUS_INGESTIONS_READ,
    permissions.CENSUS_INGESTIONS_OFFSHORE_READ,
  ]).some(Boolean);
  const hasCoveredLivesAccess = usePermissions([
    permissions.COVERED_LIVES_READ,
    permissions.COVERED_LIVES_OFFSHORE_READ,
  ]).some((p) => !!p);
  const hasDeactivationsAccess = usePermissions([
    permissions.CUSTOMER_DEACTIVATIONS_READ,
    permissions.CUSTOMER_DEACTIVATIONS_OFFSHORE_READ,
  ]).some((p) => !!p);
  const hasRateCardsAccess = usePermissions([
    permissions.CUSTOMER_RATE_CARDS_READ,
    permissions.CUSTOMER_RATE_CARDS_OFFSHORE_READ,
  ]).some((p) => !!p);
  const [canViewProviderPayRateCards, canViewCohortPayerMappings] = usePermissions([
    permissions.PROVIDER_PAY_PAYER_RATE_CARDS_READ,
    permissions.COHORT_PAYER_MAPPINGS_READ,
  ]);
  const hasCustomersAccess = usePermissions([permissions.CUSTOMERS_READ, permissions.CUSTOMERS_OFFSHORE_READ]).some(
    (p) => !!p
  );
  const hasMembersAccess = usePermissions([permissions.MEMBERS_READ, permissions.MEMBERS_OFFSHORE_READ]).some(
    (p) => !!p
  );
  const hasTrialsAccess = usePermissions([permissions.TRIALS_READ, permissions.TRIALS_OFFSHORE_READ]).some((p) => !!p);
  const hasUsersAccess = usePermissions([permissions.USERS_READ, permissions.USERS_OFFSHORE_READ]).some((p) => !!p);
  const [hasRegionsAccess] = usePermissions([permissions.ADMIN_PORTAL_REGIONS_FILES_READ]);
  const showRegionsTab = useFeatureFlag(FLAGS.SHOW_REGIONS_TAB);
  const shouldShowRegionsTab = Boolean(hasRegionsAccess && showRegionsTab);
  const scopes = user?.scopes || [];

  const isProduction = process.env.APP_ENV === 'production';
  const shouldDisplayCustomerDataIntegrations = scopes?.some((role) => role.includes(CDX_REPORTING_ADMIN));

  // Check for maintenance task runner access
  const hasMaintenanceTasksAccess = scopes?.some((scope) => scope.includes(MAINTENANCE_TASK_RUNNER));

  const hasDeactivationsEnabled = useFeatureFlag(FLAGS.DEACTIVATIONS);

  const enabledRateCards = useFeatureFlag(FLAGS.CUSTOMER_RATE_CARDS);
  const enabledCohortPayerMappings = useFeatureFlag(FLAGS.COHORT_PAYER_MAPPINGS_ENABLED);

  const menuItems: IMenuItem[] = [
    { alias: 'Home', icon: VHomeIcon, text: 'Home' },
    { alias: 'AppointmentList', icon: VCalendarIcon, text: 'Appointments', hide: !hasAppointmentsAccess },
    { alias: 'AssessmentList', icon: VAssessmentIcon, text: 'Assessments', hide: !hasAssessmentsAccess },
    { alias: 'ProviderList', icon: VProviderIcon, text: 'Caregivers', hide: !hasCareProvidersAccess },
    { alias: 'CensusIngestionList', icon: VUploadIcon, text: 'Census Ingestions', hide: !hasCensusIngestionsAccess },
    {
      alias: 'ChannelPartnerList',
      icon: MdOutlineHandshakeIcon,
      text: 'Channel Partners',
      hide: !hasChannelPartnersAccess,
    },
    { alias: 'Compass', icon: TbCompassIcon, text: PROVIDER_APP_NAME, href: getCompassUrl() },
    { alias: 'CoveredLives', icon: TbUserSearchIcon, text: 'Covered Lives', hide: !hasCoveredLivesAccess },
    {
      alias: 'CreateData',
      icon: MdFormatListBulletedAddIcon,
      text: 'Create Test Data',
      hide: isProduction,
    },
    {
      alias: 'CustomerDataIntegrations',
      icon: TbPresentationIcon,
      text: 'Customer Data Integrations',
      hide: !shouldDisplayCustomerDataIntegrations,
    },
    { alias: 'CustomerList', icon: VWorkplaceIcon, text: 'Customers', hide: !hasCustomersAccess },
    {
      alias: 'CustomerRateCards',
      icon: VLineChartIcon,
      text: 'Customer Rate Cards',
      hide: !enabledRateCards || !hasRateCardsAccess,
    },
    {
      alias: 'DeactivationsList',
      icon: VBreakupIcon,
      text: 'Deactivations',
      hide: !hasDeactivationsAccess || !hasDeactivationsEnabled,
    },
    {
      alias: 'MaintenanceTasksList',
      icon: MdBuildIcon,
      text: 'Maintenance Tasks',
      hide: !hasMaintenanceTasksAccess,
    },
    { alias: 'MemberList', icon: VAdultCareIcon, text: 'Members', hide: !hasMembersAccess },
    {
      alias: 'Regions',
      icon: VInternationalIcon,
      text: 'Regions',
      hide: !shouldShowRegionsTab,
    },
    { alias: 'SSOClients', icon: TbAccessPointIcon, text: 'SSO Portal', hide: !hasSSOAccess },
    { alias: 'TrialList', icon: TbPresentationIcon, text: 'Trials', hide: !hasTrialsAccess },
    { alias: 'UserList', icon: TbUsersIcon, text: 'Users', hide: !hasUsersAccess },
    {
      alias: 'DemoAccountList',
      icon: TbClipboardSmileIcon,
      text: 'Demo Accounts',
      hide: isProduction || !canReadDemoAccounts,
    },
    {
      alias: 'ConnectedBenefitList',
      icon: MdOutlineCircleIcon,
      text: 'Connected Benefits',
      hide: !hasConnectedBenefitsAccess,
    },
    {
      alias: 'ProviderRateCardsList',
      icon: VFinancialHelpIcon,
      text: 'Provider Rate Cards',
      hide: !canViewProviderPayRateCards,
    },
    {
      alias: 'RevenueCycle',
      icon: VInsuranceIcon,
      text: 'Revenue Cycle',
      hide: !enabledCohortPayerMappings || !canViewCohortPayerMappings,
    },
  ];

  return (
    <List
      margin="0"
      padding="8px 0 0 0"
      flex="1"
      minHeight="0"
      overflowX="hidden"
      overflowY="auto"
      sx={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
      data-testid="side-menu-items"
    >
      {menuItems
        .filter((item) => !item?.hide)
        .map(({ alias, icon, text, href }) => {
          // Compass link uses an href instead of a pathname
          const pathname = href || routes[alias]?.pathname;

          // Explicitly check for home route first, since all routes start with /admin
          const isHomeSideMenuItem = alias === 'Home';
          const isOnHomeRoute = router.asPath === routes.Home.pathname;
          const isOnNonHomeRoute = !isOnHomeRoute && router.asPath?.includes(pathname);
          const isActive = isHomeSideMenuItem ? isOnHomeRoute : isOnNonHomeRoute;

          return (
            <ListItem
              key={text}
              data-active={isActive}
              textDecoration="none"
              marginX={2}
              marginBottom={1}
              borderRadius={6}
              color="black"
              backgroundColor={isActive ? 'primary-subtle' : 'transparent'}
              borderRight="1px solid"
              borderColor={isActive ? 'primary-subtle' : 'transparent'}
              position="relative"
              whiteSpace="nowrap"
              overflow="hidden"
              _hover={{
                backgroundColor: hoverColor,
              }}
            >
              <Box
                as="span"
                position="absolute"
                width="2px"
                height="16px"
                backgroundColor={isActive ? 'primary-base' : 'transparent'}
                left="-2"
                top={2}
                borderTopRightRadius={2}
                borderBottomRightRadius={2}
              />
              <NavLink href={{ pathname }} prefetch={false} variant="navigation">
                <Flex alignItems="center" py={2} px={3} gap={3} color={isActive ? 'primary-base' : 'content-primary'}>
                  <Icon as={icon} height={4} width={4} />
                  {menuOpen && (
                    <Text textStyle="body-medium-regular" lineHeight="16px">
                      {text}
                    </Text>
                  )}
                </Flex>
              </NavLink>
            </ListItem>
          );
        })}
    </List>
  );
};
